import { useInfiniteQuery } from '@tanstack/react-query';
import { pb } from '@/core/api';
import type { Creator, CreatorStatus } from '@/types';
import { useAuth } from '@/hooks/use-auth';
import { usePipelineFilters } from './use-pipeline-filters';

const PER_PAGE = 50;

function roleFilter(
  user: ReturnType<typeof useAuth>['user'],
  isAdmin: boolean
): string {
  if (isAdmin || !user) return '';
  var role = user.role;
  if (role === 'approacher') return `assigned_approacher = "${user.id}"`;
  if (role === 'mago') return `assigned_mago = "${user.id}"`;
  return `collector = "${user.id}"`;
}

function buildFilter(
  status: CreatorStatus,
  filters: ReturnType<typeof usePipelineFilters.getState>,
  rf: string
): string {
  const parts = [`status = "${status}"`];
  if (rf) parts.push(rf);
  if (filters.category) parts.push(`category = ${filters.category}`);
  if (filters.creator_type) {
    parts.push(`creator_type = "${filters.creator_type}"`);
  }
  if (filters.collector) parts.push(`collector = "${filters.collector}"`);
  if (filters.search) {
    const s = filters.search.replace(/"/g, '\\"');
    parts.push(`(ig_username ~ "${s}" || display_name ~ "${s}")`);
  }
  return parts.join(' && ');
}

export function useColumnCreators(status: CreatorStatus) {
  const filters = usePipelineFilters();
  const { user, isAdmin } = useAuth();
  const filter = buildFilter(status, filters, roleFilter(user, isAdmin));

  return useInfiniteQuery({
    queryKey: ['pipeline', status, filter],
    queryFn: ({ pageParam = 1 }) =>
      pb.collection('leads').getList<Creator>(pageParam as number, PER_PAGE, {
        filter,
        sort: '-created',
      }),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page < last.totalPages ? last.page + 1 : undefined,
  });
}
